import { MemberUtils } from "@shared/member.utils";
import { format } from "date-fns";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import AccountIcon from "../assets/icon_account.svg";
import BadgeIcon from "../assets/icon_badge.svg";
import FamilyIcon from "../assets/icon_family.svg";
import DocumentIcon from "../assets/icon_document.svg";
import CourseIcon from "../assets/icon_course.svg";
import { Button } from "../components/common/Button";
import { ShopBagIcon } from "../components/common/ShopBagIcon";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { SHOP_ORDERS_PATH } from "./shop/ShopOrders";
import {
  PageTitle,
  PageTitleDetail,
  PageTitleSubtitle,
} from "../components/common/PageData";
import { useAuthStore } from "../stores/auth.store";
import { useSeasonStore } from "../stores/season.store";
import { useProfileOwner, useUserStore } from "../stores/user.store";

const AccountContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const MembershipCard = styled.div<{ backgroundImage?: string }>`
  background-image: url(${(props) =>
    props.backgroundImage ||
    'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"><rect width="100%" height="100%" fill="%23333"/></svg>'});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  padding: 40px 24px;
  color: white;
  position: relative;
  margin-bottom: 40px;
  overflow: hidden;
  height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
`;

const CardContent = styled.div`
  position: relative;
  z-index: 1;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #ddd;
  cursor: pointer;

  &:hover {
    background-color: #f9f9f9;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MenuIcon = styled.div`
  font-size: 24px;
`;

const MenuText = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const ChevronRight = styled.span`
  font-size: 46px;
  display: flex;
  align-items: center;
  height: 100%;
  line-height: 1;
`;

const DisclaimerText = styled.p`
  font-size: 14px;
  margin-top: 16px;
  color: #666;
  line-height: 1.4;
`;

export const Account = () => {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const profileOwner = useProfileOwner();
  const { currentSeason, latestSeason } = useSeasonStore();
  const { loadProfile } = useUserStore();
  const { profile } = useUserStore();
  const hasCoursesFeature = useFeatureFlag("courses");

  const handleLogout = async () => {
    await logout();
    await navigate("/login");
  };

  const formatRegistrationDate = (dateString?: string) => {
    if (!dateString) return "";
    return format(new Date(dateString), "dd/MM/yyyy");
  };

  const handleShowCard = () => {
    navigate("/account/card");
  };

  const currentSubscriptions =
    profile && currentSeason
      ? profile.members
          .map((member) =>
            MemberUtils.getValidSubscription(member, currentSeason),
          )
          .filter((subscription) => subscription !== null)
      : [];

  const latestSubscriptions =
    profile && latestSeason
      ? profile.members
          .map((member) => MemberUtils.getValidSubscription(member, latestSeason))
          .filter((subscription) => subscription !== null)
      : [];

  // Poll only while waiting for a subscription in the current season.
  useEffect(() => {
    if (
      currentSeason &&
      currentSubscriptions.length === 0 &&
      profileOwner
    ) {
      const interval = setInterval(() => loadProfile(), 5000);
      return () => clearInterval(interval);
    }
  }, [
    currentSeason,
    profileOwner,
    loadProfile,
    currentSubscriptions.length,
  ]);

  return (
    <AccountContainer>
      <PageTitle>IL MIO ACCOUNT</PageTitle>

      <PageTitleSubtitle>
        {profileOwner?.first_name?.toUpperCase()}{" "}
        {profileOwner?.last_name?.toUpperCase()}
      </PageTitleSubtitle>
      <PageTitleDetail>
        ISCRITTO IL {formatRegistrationDate(profileOwner?.created_at)}
      </PageTitleDetail>

      {latestSeason && latestSubscriptions.length > 0 && (
        <MembershipCard backgroundImage={latestSeason.image_url || undefined}>
          <CardOverlay />
          <CardContent>
            <Button
              onClick={handleShowCard}
              variant="secondary"
              style={{
                fontSize: "18px",
                padding: "16px 32px",
                fontWeight: "bold",
              }}
            >
              {latestSubscriptions.length === 1
                ? `MOSTRA LA MIA TESSERA ${latestSeason.name}`
                : `MOSTRA LE MIE TESSERE ${latestSeason.name}`}
            </Button>
          </CardContent>
        </MembershipCard>
      )}

      <MenuItem onClick={() => navigate("/account/edit")}>
        <MenuItemContent>
          <MenuIcon>
            <img src={AccountIcon} alt="Badge" width="32" height="32" />
          </MenuIcon>
          <MenuText>Dati Personali</MenuText>
        </MenuItemContent>
        <ChevronRight>›</ChevronRight>
      </MenuItem>

      <MenuItem onClick={() => navigate("/account/membership")}>
        <MenuItemContent>
          <MenuIcon>
            <img src={BadgeIcon} alt="Badge" width="32" height="32" />
          </MenuIcon>
          <MenuText>Tesseramento e Rinnovo</MenuText>
        </MenuItemContent>
        <ChevronRight>›</ChevronRight>
      </MenuItem>

      <MenuItem onClick={() => navigate(SHOP_ORDERS_PATH)}>
        <MenuItemContent>
          <MenuIcon>
            <ShopBagIcon />
          </MenuIcon>
          <MenuText>Ordini Shop</MenuText>
        </MenuItemContent>
        <ChevronRight>›</ChevronRight>
      </MenuItem>

      <MenuItem onClick={() => navigate("/account/members")}>
        <MenuItemContent>
          <MenuIcon>
            <img src={FamilyIcon} alt="Badge" width="32" height="32" />
          </MenuIcon>
          <MenuText>Gestione componenti famiglia</MenuText>
        </MenuItemContent>
        <ChevronRight>›</ChevronRight>
      </MenuItem>

      {hasCoursesFeature && (
        <MenuItem onClick={() => navigate("/account/courses/info")}>
          <MenuItemContent>
            <MenuIcon>
              <img src={CourseIcon} alt="Corsi" width="32" height="32" />
            </MenuIcon>
            <MenuText>Corsi e iscrizione corsi</MenuText>
          </MenuItemContent>
          <ChevronRight>›</ChevronRight>
        </MenuItem>
      )}

      <MenuItem onClick={() => navigate("/account/documents")}>
        <MenuItemContent>
          <MenuIcon>
            <img src={DocumentIcon} alt="Documents" width="32" height="32" />
          </MenuIcon>
          <MenuText>Documenti</MenuText>
        </MenuItemContent>
        <ChevronRight>›</ChevronRight>
      </MenuItem>

      <Button
        fullWidth
        variant="primary"
        onClick={handleLogout}
        style={{ marginTop: "16px" }}
      >
        LOGOUT
      </Button>

      <DisclaimerText>
        Se desideri cancellare la tua iscrizione e i tuoi dati, scrivi a{" "}
        <a
          href="mailto:info@co2snowboarding.it"
          style={{ color: "#000", textDecoration: "underline" }}
        >
          info@co2snowboarding.it
        </a>{" "}
        o contattaci direttamente.
      </DisclaimerText>
    </AccountContainer>
  );
};
